import { useCallback, useEffect, useRef, useState } from 'react';

import {
  track,
  AnalyticsEvent,
  type WarningSource,
} from '@/lib/analytics/tracker';
import { checkBookingPrecheck, BookingBlockReason } from '@/services/order';

export type BookingWarning<T> =
  | { kind: 'rebook'; spots: number; payload: T }
  | {
      kind: 'closed';
      // Both come off an unvalidated response cast, so neither is a guarantee.
      reason: BookingBlockReason;
      message: string | null;
      spots: number;
      payload: T;
    };

// The two events accept different action literals, so the neutral name is
// mapped per branch rather than passed through.
const trackWarningAction = (
  warning: BookingWarning<unknown>,
  action: 'primary' | 'dismissed',
  source: WarningSource,
  className: string,
  classId: string
) => {
  const base = { source, class_name: className, class_id: classId };
  if (warning.kind === 'rebook') {
    track({
      event: AnalyticsEvent.REBOOK_WARNING_ACTION,
      properties: {
        ...base,
        action: action === 'primary' ? 'confirmed' : 'dismissed',
      },
    });
    return;
  }
  track({
    event: AnalyticsEvent.BOOKING_CLOSED_ACTION,
    properties: {
      ...base,
      action: action === 'primary' ? 'see_other_times' : 'dismissed',
      reason: warning.reason,
    },
  });
};

// Matches the design system's panel-hide keyframe.
const MODAL_EXIT_MS = 250;

type GuardParams<T> = {
  email: string;
  slotId: string | null;
  overrideSlotId?: string | null;
  startTime: string;
  instructorScheduleId?: string;
  timezone?: string;
  payload: T;
};

export const useBookingGuard = <T,>(
  source: WarningSource,
  classInfo: { className: string; classId: string },
  onProceed: (payload: T) => void,
  onSeeOtherTimes: () => void
) => {
  const [warning, setWarning] = useState<BookingWarning<T> | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const handledRef = useRef(false);
  const { className, classId } = classInfo;

  // Held past the close so the exit animation still has data, then dropped:
  // the class page's confirm does not navigate, so without this the modal
  // stays mounted on a slot the user has already moved past.
  useEffect(() => {
    if (isOpen) return;
    const timer = setTimeout(() => setWarning(null), MODAL_EXIT_MS);
    return () => clearTimeout(timer);
  }, [isOpen]);

  const guardBooking = useCallback(
    async ({
      email,
      slotId,
      overrideSlotId,
      startTime,
      instructorScheduleId,
      timezone,
      payload,
    }: GuardParams<T>) => {
      setIsChecking(true);
      try {
        // The backend rejects a partial trio outright, so a caller missing one
        // field must ask the duplicate-only question rather than a broken one.
        const ruleFields =
          classId && instructorScheduleId && timezone
            ? {
                class_id: classId,
                instructor_schedule_id: instructorScheduleId,
                timezone,
              }
            : undefined;

        const precheck = await checkBookingPrecheck({
          email,
          slot_id: slotId,
          override_slot_id: overrideSlotId ?? null,
          start_time: startTime,
          ...ruleFields,
        });
        // Closed wins over already-booked: rebooking a slot that fell out of
        // the booking window would only fail at order creation.
        // `=== false` (not `!bookable`): a stale backend response without the
        // field must never enter the closed branch.
        if (precheck?.bookable === false) {
          handledRef.current = false;
          setWarning({
            kind: 'closed',
            reason: precheck.block_reason,
            message: precheck.block_message,
            spots: precheck.booked_spots,
            payload,
          });
          setIsOpen(true);
          track({
            event: AnalyticsEvent.BOOKING_CLOSED_SHOWN,
            properties: {
              class_name: className,
              class_id: classId,
              source,
              reason: precheck.block_reason,
            },
          });
          return true;
        }
        if (!precheck?.already_booked) {
          setWarning(null);
          return false;
        }
        handledRef.current = false;
        setWarning({ kind: 'rebook', spots: precheck.booked_spots, payload });
        setIsOpen(true);
        track({
          event: AnalyticsEvent.REBOOK_WARNING_SHOWN,
          properties: {
            class_name: className,
            class_id: classId,
            source,
            spots_booked: precheck.booked_spots,
          },
        });
        return true;
      } finally {
        setIsChecking(false);
      }
    },
    [source, className, classId]
  );

  const handleConfirm = useCallback(() => {
    if (!warning) return;
    handledRef.current = true;
    trackWarningAction(warning, 'primary', source, className, classId);
    setIsOpen(false);
    if (warning.kind === 'rebook') onProceed(warning.payload);
    else onSeeOtherTimes();
  }, [warning, source, className, classId, onProceed, onSeeOtherTimes]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) return;
      // Cancel fires onOpenChange twice (explicit call + Modal.Close); the ref
      // keeps the dismissed event single.
      if (!handledRef.current && warning) {
        handledRef.current = true;
        trackWarningAction(warning, 'dismissed', source, className, classId);
      }
      setIsOpen(false);
    },
    [warning, source, className, classId]
  );

  return {
    warning,
    isOpen,
    isChecking,
    guardBooking,
    handleConfirm,
    handleOpenChange,
  };
};
